import React, { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';

type Reference = {
  id: string;
  name: string;
  logoPath?: string;
  website?: string;
  contactPerson?: string;
  testimonial?: string;
  projectName?: string;
  projectDescription?: string;
  projectImage1?: string;
  projectImage2?: string;
  projectImage3?: string;
  projectImage4?: string;
  courseId?: string;
  isActive?: boolean;
  status: 'active' | 'deleted'; // Now required with default 'active'
};

type SortField = 'name' | 'website' | 'contactPerson' | 'projectName' | 'isActive';
type SortDirection = 'asc' | 'desc';

interface AdminReferencesProps {
  onEditReference: (referenceId: string) => void;
}

const client = generateClient<Schema>({ authMode: 'userPool' });

const AdminReferences: React.FC<AdminReferencesProps> = ({ onEditReference }) => {
  const [references, setReferences] = useState<Reference[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);


  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Deleted items state
  const [showDeletedTable, setShowDeletedTable] = useState<boolean>(false);

  const loadReferences = async () => {
    try {
      setLoading(true);
      console.log('📋 Loading references from database...');
      
      // Fetch ALL references with pagination
      let allReferences: any[] = [];
      let nextToken: string | null | undefined = null;
      let hasErrors = false;
      let errorMessages: string[] = [];
      
      do {
        const response: any = await client.models.Reference.list({
          limit: 1000,
          nextToken: nextToken as any
        });
        
        if (response.errors && response.errors.length) {
          hasErrors = true;
          errorMessages = [...errorMessages, ...response.errors.map((err: any) => err?.message || String(err))];
        }
        
        allReferences = [...allReferences, ...(response.data || [])];
        nextToken = response.nextToken;
        
        console.log(`📊 AdminReferences: Fetched ${response.data?.length || 0} references (total: ${allReferences.length})`);
      } while (nextToken);
      
      if (hasErrors) {
        console.error('❌ Errors loading references:', errorMessages);
        setError(errorMessages.join(', '));
        setReferences([]);
      } else {
        console.log('✅ Total references loaded:', allReferences.length);
        console.log('📊 Sample references with status:', allReferences.slice(0, 3).map((ref: any) => ({
          id: ref.id,
          name: ref.name,
          status: ref.status,
          hasStatus: 'status' in ref
        })));
        setError(null);
        setReferences(allReferences as unknown as Reference[]);
      }
    } catch (e: any) {
      console.error('❌ Exception loading references:', e);
      setError(e?.message || String(e));
      setReferences([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReferences();
  }, []);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDeleteReference = async (referenceId: string) => {
    const reference = references.find(r => r.id === referenceId);
    if (reference) {
      try {
        setLoading(true);
        console.log('🗑️ Attempting to soft delete reference:', referenceId);
        
        // Soft delete: set status to 'deleted' instead of actually deleting
        const result = await client.models.Reference.update({ 
          id: referenceId,
          status: 'deleted'
        });
        
        console.log('✅ Soft delete result:', result);
        
        // Refresh the list
        loadReferences();
      } catch (err) {
        console.error('❌ Error deleting reference:', err);
        console.error('Error details:', JSON.stringify(err, null, 2));
      } finally {
        setLoading(false);
      }
    }
  };



  // Add restore function for deleted references
  const handleRestoreReference = async (referenceId: string, referenceName: string) => {
    try {
      setLoading(true);
      await client.models.Reference.update({ 
        id: referenceId,
        status: 'active'
      });
      

      
      // Refresh the list
      loadReferences();
      

    } catch (err) {
      console.error('Error restoring reference:', err);

    } finally {
      setLoading(false);
    }
  };

  // Separate active and deleted references
  // Treat references without status field as active (for backward compatibility)
  const activeReferences = references.filter(ref => ref.status !== 'deleted');
  const deletedReferences = references.filter(ref => ref.status === 'deleted');
  
  // Debug logging
  console.log('🔍 Filtering references:');
  console.log('  Total references:', references.length);
  console.log('  Active references:', activeReferences.length);
  console.log('  Deleted references:', deletedReferences.length);
  console.log('  References with status field:', references.filter(r => 'status' in r && r.status !== undefined).length);
  console.log('  Sample reference statuses:', references.slice(0, 5).map(r => ({ 
    name: r.name, 
    status: r.status, 
    hasStatus: 'status' in r,
    statusType: typeof r.status
  })));
  
  if (deletedReferences.length > 0) {
    console.log('🗑️ Found deleted references:', deletedReferences.map(r => ({ name: r.name, status: r.status })));
  }

  // Filter active references based on search term
  const filteredReferences = activeReferences.filter(ref => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      ref.name?.toLowerCase().includes(searchLower) ||
      ref.website?.toLowerCase().includes(searchLower) ||
      ref.contactPerson?.toLowerCase().includes(searchLower) ||
      ref.projectName?.toLowerCase().includes(searchLower) ||
      ref.projectDescription?.toLowerCase().includes(searchLower)
    );
  });

  // Sort filtered references based on selected field and direction
  const sortedReferences = [...filteredReferences].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    // Handle boolean sorting (isActive)
    if (sortField === 'isActive') {
      aValue = aValue ? 1 : 0;
      bValue = bValue ? 1 : 0;
    }

    // Handle string sorting
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    // Handle null/undefined values
    if (!aValue && !bValue) return 0;
    if (!aValue) return sortDirection === 'asc' ? 1 : -1;
    if (!bValue) return sortDirection === 'asc' ? -1 : 1;

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination calculations
  const totalItems = sortedReferences.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReferences = sortedReferences.slice(startIndex, endIndex);

  // Reset to page 1 if current page is out of bounds
  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(1);
  }

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };



  // Inject search into header (fixed to prevent recreation on every keystroke)
  useEffect(() => {
    const container = document.getElementById('header-selector-container');
    if (container) {
      // Check if search field already exists
      let searchInput = container.querySelector('#referencesSearch') as HTMLInputElement;
      let clearButton = container.querySelector('#referencesClear') as HTMLButtonElement;
      
      if (!searchInput) {
        // Create the search field only if it doesn't exist
        container.innerHTML = `
          <div style="display: flex; align-items: center; gap: 12px;">
            <input
              id="referencesSearch"
              type="text"
              placeholder="Search references..."
              style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 250px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
            />
            <button
              id="referencesClear"
              style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; background: rgba(255, 255, 255, 0.95); color: #374151; cursor: pointer; font-weight: 500; display: none;"
            >
              Clear
            </button>
          </div>
        `;

        // Get the newly created elements
        searchInput = container.querySelector('#referencesSearch') as HTMLInputElement;
        clearButton = container.querySelector('#referencesClear') as HTMLButtonElement;

        // Add event listeners only once
        if (searchInput) {
          searchInput.addEventListener('input', (e) => {
            const value = (e.target as HTMLInputElement).value;
            setSearchTerm(value);
            setCurrentPage(1);
            
            // Show/hide clear button
            if (clearButton) {
              clearButton.style.display = value ? 'block' : 'none';
            }
          });
          
          searchInput.addEventListener('focus', (e) => {
            (e.target as HTMLInputElement).style.borderColor = 'rgba(255, 255, 255, 0.6)';
            (e.target as HTMLInputElement).style.boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.2)';
          });
          
          searchInput.addEventListener('blur', (e) => {
            (e.target as HTMLInputElement).style.borderColor = 'rgba(255, 255, 255, 0.3)';
            (e.target as HTMLInputElement).style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
          });
        }

        if (clearButton) {
          clearButton.addEventListener('click', () => {
            setSearchTerm('');
            setCurrentPage(1);
            if (searchInput) {
              searchInput.value = '';
              searchInput.focus();
            }
            clearButton.style.display = 'none';
          });
        }
      }
      
      // Update the input value and clear button visibility without recreating
      if (searchInput) {
        searchInput.value = searchTerm;
      }
      if (clearButton) {
        clearButton.style.display = searchTerm ? 'block' : 'none';
      }
    }

    return () => {
      // Cleanup only when component unmounts
      const container = document.getElementById('header-selector-container');
      if (container) {
        container.innerHTML = '';
      }
    };
  }, []); // Empty dependency array - only run once

  // Separate effect to update the search input value without recreating the DOM
  useEffect(() => {
    const searchInput = document.getElementById('referencesSearch') as HTMLInputElement;
    const clearButton = document.getElementById('referencesClear') as HTMLButtonElement;
    
    if (searchInput && searchInput.value !== searchTerm) {
      searchInput.value = searchTerm;
    }
    if (clearButton) {
      clearButton.style.display = searchTerm ? 'block' : 'none';
    }
  }, [searchTerm]);

  return (
    <div className="admin-content-card" style={{ paddingBottom: '60px' }}>


      

      
      {loading ? (
        <div style={{ padding: 24 }}>Loading…</div>
      ) : error ? (
        <div style={{ padding: 24, color: '#dc2626' }}>Error: {error}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                  Company {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('website')} style={{ cursor: 'pointer' }}>
                  Website {sortField === 'website' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('contactPerson')} style={{ cursor: 'pointer' }}>
                  Contact {sortField === 'contactPerson' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('projectName')} style={{ cursor: 'pointer' }}>
                  Project {sortField === 'projectName' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('isActive')} style={{ cursor: 'pointer' }}>
                  Status {sortField === 'isActive' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedReferences.map((ref) => (
                <tr key={ref.id} style={{ height: '60px' }}>
                  <td style={{ 
                    verticalAlign: 'middle', 
                    padding: '12px 16px',
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    <div title={ref.name}>{ref.name}</div>
                  </td>
                  <td style={{ 
                    verticalAlign: 'middle', 
                    padding: '12px 16px',
                    maxWidth: '250px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {ref.website ? (
                      <a 
                        href={ref.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        title={ref.website}
                        style={{ color: '#008F99', textDecoration: 'none' }}
                        onMouseOver={(e) => (e.target as HTMLAnchorElement).style.textDecoration = 'underline'}
                        onMouseOut={(e) => (e.target as HTMLAnchorElement).style.textDecoration = 'none'}
                      >
                        {ref.website}
                      </a>
                    ) : '—'}
                  </td>
                  <td style={{ 
                    verticalAlign: 'middle', 
                    padding: '12px 16px',
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    <div title={ref.contactPerson || '—'}>{ref.contactPerson || '—'}</div>
                  </td>
                  <td style={{ 
                    verticalAlign: 'middle', 
                    padding: '12px 16px',
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    <div title={ref.projectName || '—'}>{ref.projectName || '—'}</div>
                  </td>
                  <td style={{ 
                    verticalAlign: 'middle', 
                    padding: '12px 16px',
                    width: '100px'
                  }}>
                    <span className={`status-badge ${ref.isActive ? 'active' : 'inactive'}`}>
                      {ref.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions" style={{ 
                    verticalAlign: 'middle', 
                    padding: '12px 16px',
                    width: '180px',
                    whiteSpace: 'nowrap'
                  }}>
                    <button className="admin-btn" onClick={() => onEditReference(ref.id)}>Edit</button>
                    <button 
                      className="admin-btn danger" 
                      onClick={() => handleDeleteReference(ref.id)}
                      style={{ marginLeft: '8px' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Pagination Controls */}
      {!loading && !error && totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} references
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              style={{
                padding: '4px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: currentPage === 1 ? '#f3f4f6' : 'white',
                color: currentPage === 1 ? '#9ca3af' : '#374151',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Previous
            </button>
            
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: currentPage === pageNum ? '#008F99' : 'white',
                    color: currentPage === pageNum ? 'white' : '#374151',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: currentPage === pageNum ? '600' : '400'
                  }}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: currentPage === totalPages ? '#f3f4f6' : 'white',
                color: currentPage === totalPages ? '#9ca3af' : '#374151',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
      
      {/* Pagination Info for single page */}
      {!loading && !error && totalPages <= 1 && totalItems > 0 && (
        <div style={{
          padding: '12px 24px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          fontSize: '14px',
          color: '#6b7280',
          textAlign: 'center'
        }}>
          Showing all {totalItems} references
        </div>
      )}
      
      {/* Deleted References Section - Separate Table Below */}
      {!loading && !error && (
        <div className="admin-content-card" style={{ marginTop: '32px' }}>
          <div 
            onClick={() => setShowDeletedTable(!showDeletedTable)}
            style={{
              padding: '16px 24px',
              backgroundColor: '#f9fafb',
              borderBottom: showDeletedTable ? '1px solid #e5e7eb' : 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                Deleted References ({deletedReferences.length})
              </span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                Click to {showDeletedTable ? 'hide' : 'show'}
              </span>
            </div>
            <span style={{ fontSize: '18px', color: '#6b7280', transform: showDeletedTable ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              ▼
            </span>
          </div>
          
          {showDeletedTable && (
            <div style={{ overflowX: 'auto' }}>
              {deletedReferences.length === 0 ? (
                <div style={{ 
                  padding: '48px 24px', 
                  textAlign: 'center', 
                  color: '#6b7280',
                  backgroundColor: '#fefefe'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗑️</div>
                  <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No deleted references</h3>
                  <p style={{ margin: 0 }}>
                    When you delete references, they will appear here and can be restored.
                  </p>
                </div>
              ) : (
                <table className="admin-table" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Website</th>
                      <th>Contact</th>
                      <th>Project</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deletedReferences.map((ref) => (
                      <tr key={ref.id} style={{ height: '60px', backgroundColor: '#fefefe' }}>
                        <td style={{ 
                          verticalAlign: 'middle', 
                          padding: '12px 16px',
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          opacity: 0.7
                        }}>
                          <div title={ref.name}>{ref.name}</div>
                        </td>
                        <td style={{ 
                          verticalAlign: 'middle', 
                          padding: '12px 16px',
                          maxWidth: '250px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          opacity: 0.7
                        }}>
                          {ref.website ? (
                            <a 
                              href={ref.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              title={ref.website}
                              style={{ color: '#dc2626', textDecoration: 'none' }}
                              onMouseOver={(e) => (e.target as HTMLAnchorElement).style.textDecoration = 'underline'}
                              onMouseOut={(e) => (e.target as HTMLAnchorElement).style.textDecoration = 'none'}
                            >
                              {ref.website}
                            </a>
                          ) : '—'}
                        </td>
                        <td style={{ 
                          verticalAlign: 'middle', 
                          padding: '12px 16px',
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          opacity: 0.7
                        }}>
                          <div title={ref.contactPerson || '—'}>{ref.contactPerson || '—'}</div>
                        </td>
                        <td style={{ 
                          verticalAlign: 'middle', 
                          padding: '12px 16px',
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          opacity: 0.7
                        }}>
                          <div title={ref.projectName || '—'}>{ref.projectName || '—'}</div>
                        </td>
                        <td style={{ 
                          verticalAlign: 'middle', 
                          padding: '12px 16px',
                          width: '120px',
                          whiteSpace: 'nowrap'
                        }}>
                          <button 
                            className="admin-btn" 
                            onClick={() => handleRestoreReference(ref.id, ref.name)}
                            style={{ 
                              backgroundColor: '#10b981', 
                              borderColor: '#10b981', 
                              color: 'white' 
                            }}
                          >
                            Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* Blank footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: '280px',
        right: 0,
        background: 'white',
        borderTop: '1px solid #e5e7eb',
        boxShadow: '0 -4px 10px rgba(0, 0, 0, 0.06)',
        zIndex: 200,
        padding: '12px 24px',
        height: '48px'
      }} />
    </div>
  );
};

export default AdminReferences;


